import * as React from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  type ColumnDef, flexRender, getCoreRowModel, useReactTable,
  type VisibilityState,
} from '@tanstack/react-table'
import {
  ArrowDown, ArrowUp, ArrowUpDown,
  Columns3, Copy, Download, Eye, Heart, MoreHorizontal, Pencil, Plus,
  RefreshCw, Search, Share2, Trash2, X,
} from 'lucide-react'
import {
  useAdminBulkDeleteDomains, useAdminCreateDomain, useAdminDeleteDomain,
  useAdminLookupDomain, useAdminUpdateDomain,
  useCreateWatcher, useDomains, useFavoriteDomain, useRegistrars, useTagVocabulary, useTldCounts,
  useUnfavoriteDomain,
  type AdminDomainInput, type DomainListParams,
} from '@/lib/queries'
import { useAuth } from '@/lib/auth'
import { download, ApiError } from '@/lib/api'
import type { Domain } from '@/lib/types'
import { PageHeader } from '@/components/layout/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox, Label } from '@/components/ui/misc'
import { Tooltip } from '@/components/ui/tooltip'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states'
import {
  FreshnessCell, GfwBadge, StatusBadge, TierBadge,
  sourceLabel,
} from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { DomainDetailDrawer } from '@/components/layout/DomainDetailDrawer'
import { DateTimePicker } from '@/components/ui/date-picker'
import { FilterPopover, FilterSelect, FilterToggle } from '@/components/ui/filter-select'
import { cn, formatDate, formatDateTime, formatNumber } from '@/lib/utils'

const STATUS_OPTIONS = [
  { value: 'registered', label: '已注册' },
  { value: 'expired', label: '已过期' },
  { value: 'redemptionPeriod', label: '赎回期' },
  { value: 'pendingDelete', label: '删除等待期' },
  { value: 'available', label: '可注册' },
  { value: 'reserved', label: '保留' },
  { value: 'unknown', label: '未知' },
]

/**
 * The Chinese-semantics filters, each a boolean the server already computed.
 *
 * Presented as one menu because they are alternative readings of the same
 * question — "does this name mean something in Chinese" — and a user picking
 * 双拼 is not also going to want 吉祥号 on the same query.
 */
const ZH_OPTIONS = [
  { value: 'lucky', label: '吉祥号', description: '不含 4，且成句谐音或全 6/8/9', param: 'zh_lucky', on: 'true' },
  { value: 'abbr', label: '声母缩写', description: '每个字母都是合法声母', param: 'zh_initial_abbr', on: 'true' },
  { value: 'shuangpin', label: '双拼', description: '存在两音节读法', param: 'zh_syllables', on: '2' },
  { value: 'dict', label: '词典词', description: '整体是一个真实中文词，而非只是能拼读', param: 'zh_in_dictionary', on: 'true' },
]

const CHARSET_OPTIONS = [
  { value: 'digits', label: '纯数字' },
  { value: 'letters', label: '纯字母' },
  { value: 'alphanumeric', label: '字母数字' },
  { value: 'hyphenated', label: '含连字符' },
  { value: 'idn', label: 'IDN' },
]


/**
 * Which table columns are clickable-to-sort, and what each one asks the server
 * for. `desc` is the direction the first click applies, chosen per column to be
 * the one worth looking at: newest confirmation, longest registered, soonest to
 * drop.
 *
 * `invert` marks a column that reads its data backwards from the column the
 * server sorts on. 已注册 counts up as registered_at counts down, so without the
 * flag its arrow would point the opposite way from the order it produced. Two
 * columns can therefore share one sort key and both light up at once — 注册时间
 * ascending and 已注册 descending are not two orderings that happen to agree,
 * they are the same ordering read two ways, and showing only one as active
 * would be the lie.
 */
const SORTABLE_COLUMNS: Record<string, { key: string; desc: boolean; invert?: boolean }> = {
  domain: { key: 'domain', desc: false },
  registrar: { key: 'current_registrar', desc: false },
  length: { key: 'label_length', desc: false },
  status: { key: 'current_status', desc: false },
  registered_at: { key: 'registered_at', desc: true },
  dates: { key: 'expires_at', desc: false },
  registered_years: { key: 'registered_at', desc: true, invert: true },
  expired_days: { key: 'expires_at', desc: true, invert: true },
  release_estimate: { key: 'drop_estimate_at', desc: false },
  freshness: { key: 'last_resolved_at', desc: true },
  first_seen: { key: 'first_seen_at', desc: true },
  seen: { key: 'last_seen_at', desc: true },
  score: { key: 'value_score', desc: true },
  zh: { key: 'zh_pinyin_syllables', desc: false },
}

/** Translates a column's own reading direction into a server order. */
function serverOrder(column: { desc: boolean; invert?: boolean }, desc: boolean): 'asc' | 'desc' {
  return desc !== Boolean(column.invert) ? 'desc' : 'asc'
}

/**
 * Whole years a name has been registered, or null when unknown or under a year.
 *
 * Its own column, next to the registration date. It briefly sat inside the
 * status cell, where a parenthetical inherits the meaning of the word before
 * it: 已注册（3 年）reads correctly, 删除等待期（3 年）reads as three years
 * spent in pendingDelete — the opposite of what the number says.
 */
function registeredYears(at?: string): number | null {
  if (!at) return null
  const years = (Date.now() - Date.parse(at)) / (365.25 * 24 * 3600 * 1000)
  return years >= 1 ? Math.floor(years) : null
}

/**
 * The statuses where "days since expiry" is worth showing: the name has passed
 * its expiry and is still moving toward the drop.
 */
const DROPPING_STATUSES = new Set(['expired', 'redemptionPeriod', 'pendingDelete'])

/**
 * Whole days since a name's expiry passed, or null if it has not.
 *
 * Only counted forwards; a future expiry belongs to the release countdown
 * column next to it. The caller decides which statuses this is meaningful for —
 * an expiry date in the past does not by itself mean a name is expired.
 */
function expiredDays(at?: string): number | null {
  if (!at) return null
  const days = (Date.now() - Date.parse(at)) / (24 * 3600 * 1000)
  return days >= 1 ? Math.floor(days) : null
}

/**
 * A printable name for a column in the visibility picker.
 *
 * Only a string header can be printed directly: a header defined as a render
 * function stringifies to its own source, and the picker listed a column whose
 * name was a line of JSX. Such columns carry meta.label instead.
 */
function columnLabel(def: ColumnDef<Domain>): string {
  if (typeof def.header === 'string') return def.header
  const meta = def.meta as { label?: string } | undefined
  return meta?.label ?? String(def.id ?? '')
}

/**
 * What the 中文语义 column knows about one name, spelled out.
 *
 * The cell has room for a reading and a badge; everything that explains the
 * reading lives here. The two sources are kept visibly apart — the rules are
 * reproducible and the model is not, so a reader deciding whether to trust
 * 啊 needs to know which one said it.
 */
function ZHDetail({ d }: { d: Domain }) {
  const ai = d.zh_ai_reading || d.zh_ai_meaning || d.zh_ai_english || d.zh_ai_note
  if (!ai && !d.zh_note) return null
  return (
    <div className="max-w-[280px] space-y-1.5 text-xs">
      {ai && (
        <div className="space-y-0.5">
          <div className="font-medium">
            模型释义{d.zh_ai_reading ? `：${d.zh_ai_reading}` : ''}
          </div>
          {d.zh_ai_meaning && <div className="opacity-90">{d.zh_ai_meaning}</div>}
          {/* Whether the string is itself an English word — not a translation
              of the Chinese meaning. The distinction is the whole point of the
              field, so the label has to carry it. */}
          {d.zh_ai_english && <div className="opacity-90">英文词：{d.zh_ai_english}</div>}
          {d.zh_ai_note && <div className="opacity-75">{d.zh_ai_note}</div>}
        </div>
      )}
      {d.zh_note && (
        <div className={ai ? 'border-t border-white/15 pt-1.5 opacity-75' : ''}>
          {ai && <span className="mr-1 opacity-75">规则：</span>}
          {d.zh_note}
        </div>
      )}
    </div>
  )
}

/**
 * The tags a label carries, as the operator defined them.
 *
 * Rendered plainly rather than through FeatureTags: that component translates a
 * closed vocabulary of built-in codes into Chinese and hides tags implied by
 * others. These strings are already the words a person chose, so translating or
 * second-guessing them would only be a way to show something they did not write.
 */
function LabelTags({ tags, max = 3 }: { tags?: string[]; max?: number }) {
  if (!tags?.length) return <span className="text-xs text-muted-foreground">—</span>
  const shown = tags.slice(0, max)
  const rest = tags.slice(max)
  return (
    <span className="flex flex-wrap gap-1">
      {shown.map((t) => (
        <Badge key={t} variant="secondary" className="font-normal">{t}</Badge>
      ))}
      {rest.length > 0 && (
        <Tooltip content={rest.join('、')}>
          <Badge variant="outline" className="font-normal">+{rest.length}</Badge>
        </Tooltip>
      )}
    </span>
  )
}

const COLUMN_PREF_KEY = 'ndm.domains.columns'

export default function DomainLibraryPage() {
  const [params, setParams] = useSearchParams()
  const { user } = useAuth()
  const toast = useToast()
  const createWatcher = useCreateWatcher()
  const favoriteDomain = useFavoriteDomain()
  const unfavoriteDomain = useUnfavoriteDomain()
  const bulkDelete = useAdminBulkDeleteDomains()

  const [detailId, setDetailId] = React.useState<string | null>(null)
  const [createOpen, setCreateOpen] = React.useState(false)
  const [editingDomain, setEditingDomain] = React.useState<Domain | null>(null)
  const [deletingDomain, setDeletingDomain] = React.useState<Domain | null>(null)
  const [confirmBulkDelete, setConfirmBulkDelete] = React.useState(false)
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = React.useState('')
  const [selected, setSelected] = React.useState<Record<string, boolean>>({})
  const [showColumns, setShowColumns] = React.useState(false)
  const [searchDraft, setSearchDraft] = React.useState(params.get('q') ?? '')
  const [cursorStack, setCursorStack] = React.useState<string[]>([])

  // Search as you type, debounced. The submit button is gone, so waiting for
  // Enter would leave the box looking like it does nothing; the delay keeps one
  // query per pause instead of one per keystroke. Enter still works — it simply
  // arrives at the same place sooner.
  const activeQuery = params.get('q') ?? ''
  React.useEffect(() => {
    if (searchDraft === activeQuery) return
    const t = setTimeout(() => updateParam('q', searchDraft || undefined), 350)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft, activeQuery])

  // Suffix choices come from what the library holds, not from the configured
  // TLD table: offering a suffix with no domains behind it is a dead end.
  const { data: tldData } = useTldCounts()
  const { data: tagVocab } = useTagVocabulary()
  const tldOptions = React.useMemo(
    () => (tldData?.tlds ?? []).map((t) => ({
      value: t.tld,
      label: `.${t.tld}`,
      badge: formatNumber(t.count),
    })),
    [tldData],
  )

  // Registrar choices come from what the library actually holds; the count is
  // shown because it is what makes one worth filtering on.
  const { data: registrarData } = useRegistrars({ linked: true })
  const registrarOptions = React.useMemo(
    () => (registrarData?.registrars ?? []).map((r) => ({
      // Filter by directory id, not by name: a registrar's domains are spread
      // across every spelling a registry ever returned, and the id is what
      // gathers them.
      value: r.id,
      label: r.short_name || r.name,
      description: r.aliases && r.aliases.length > 1 ? `${r.aliases.length} 个别名` : undefined,
      badge: formatNumber(r.domain_count),
    })),
    [registrarData],
  )

  // Column visibility is a saved user preference (§5.4).
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(() => {
    try {
      return JSON.parse(localStorage.getItem(COLUMN_PREF_KEY) ?? '{}')
    } catch {
      return {}
    }
  })
  React.useEffect(() => {
    localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(columnVisibility))
  }, [columnVisibility])

  // The URL is the single source of truth for the query, so a filtered view is
  // copyable and shareable (§5.4). Only the caller's own object ids ever
  // appear in it.
  const query: DomainListParams = React.useMemo(
    () => ({
      q: params.get('q') || undefined,
      tld: params.getAll('tld'),
      zh_lucky: params.get('zh_lucky') === 'true' ? true : undefined,
      zh_initial_abbr: params.get('zh_initial_abbr') === 'true' ? true : undefined,
      zh_syllables: params.get('zh_syllables') ? Number(params.get('zh_syllables')) : undefined,
      zh_in_dictionary: params.get('zh_in_dictionary') === 'true' ? true : undefined,
      status: params.getAll('status'),
      charset: params.getAll('charset'),
      feature: params.getAll('feature'),
      tag: params.getAll('tag'),
      length_min: params.get('length_min') ? Number(params.get('length_min')) : undefined,
      length_max: params.get('length_max') ? Number(params.get('length_max')) : undefined,
      watched: params.get('watched') === 'true' ? true : undefined,
      favorite: params.get('favorite') === 'true' ? true : undefined,
      rule_id: params.get('rule_id') || undefined,
      match_status: params.get('match_status') || undefined,
      registrar_id: params.getAll('registrar_id'),
      expires_from: params.get('expires_from') || undefined,
      expires_to: params.get('expires_to') || undefined,
      drop_from: params.get('drop_from') || undefined,
      drop_to: params.get('drop_to') || undefined,
      last_seen_from: params.get('last_seen_from') || undefined,
      last_seen_to: params.get('last_seen_to') || undefined,
      sort: params.get('sort') || 'last_seen_at',
      order: params.get('order') || undefined,
      limit: 50,
      cursor: params.get('cursor') || undefined,
    }),
    [params],
  )

  const { data, isLoading, isFetching, error, refetch } = useDomains(query)

  function updateParam(key: string, value: string | string[] | undefined) {
    const next = new URLSearchParams(params)
    next.delete(key)
    next.delete('cursor') // any filter change restarts pagination
    if (Array.isArray(value)) {
      for (const v of value) next.append(key, v)
    } else if (value) {
      next.set(key, value)
    }
    setParams(next, { replace: true })
    setCursorStack([])
  }

  /**
   * Sets several params at once. A range filter has to apply both ends
   * together: writing them one at a time would fire two navigations and run a
   * query against a half-applied range in between.
   */
  function updateParams(patch: Record<string, string | undefined>) {
    const next = new URLSearchParams(params)
    next.delete('cursor')
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value)
      else next.delete(key)
    }
    setParams(next, { replace: true })
    setCursorStack([])
  }

  /**
   * Reads how a sortable column is currently ordered, in that column's own
   * terms rather than the server's. Returns null when the list is ordered by
   * something else.
   */
  function columnSort(columnID: string): { desc: boolean } | null {
    const column = SORTABLE_COLUMNS[columnID]
    if (!column || column.key !== query.sort) return null
    // No explicit order means the server's default for this key, which the UI
    // does not track — treat the column's own first-click direction as active,
    // since that is what a click would have produced.
    const desc = query.order ? query.order === 'desc' : serverOrder(column, column.desc) === 'desc'
    return { desc: desc !== Boolean(column.invert) }
  }

  function toggleSort(columnID: string) {
    const column = SORTABLE_COLUMNS[columnID]
    if (!column) return
    const active = columnSort(columnID)
    const desc = active ? !active.desc : column.desc
    const next = new URLSearchParams(params)
    next.set('sort', column.key)
    next.set('order', serverOrder(column, desc))
    next.delete('cursor') // a new ordering invalidates the cursor
    setParams(next, { replace: true })
    setCursorStack([])
  }

  function toggleMulti(key: string, value: string) {
    const current = params.getAll(key)
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    updateParam(key, next)
  }

  const activeFilters =
    params.getAll('tld').length + params.getAll('status').length +
    params.getAll('charset').length + params.getAll('feature').length +
    params.getAll('registrar_id').length + params.getAll('tag').length +
    ZH_OPTIONS.filter((o) => params.get(o.param) === o.on).length +
    (params.get('q') ? 1 : 0) + (params.get('favorite') ? 1 : 0) +
    (params.get('length_min') ? 1 : 0) + (params.get('length_max') ? 1 : 0) +
    // A range counts once however many of its two ends are set.
    DATE_FIELDS.filter((f) => params.get(f.from) || params.get(f.to)).length

  const columns = React.useMemo<ColumnDef<Domain>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getRowModel().rows.length > 0 &&
              table.getRowModel().rows.every((r) => selected[r.original.id])
            }
            onCheckedChange={(checked) => {
              const next = { ...selected }
              for (const row of table.getRowModel().rows) {
                if (checked) next[row.original.id] = true
                else delete next[row.original.id]
              }
              setSelected(next)
            }}
            aria-label="全选本页"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={Boolean(selected[row.original.id])}
            onCheckedChange={(checked) => {
              setSelected((prev) => {
                const next = { ...prev }
                if (checked) next[row.original.id] = true
                else delete next[row.original.id]
                return next
              })
            }}
            aria-label={`选择 ${row.original.domain_ascii}`}
          />
        ),
        enableHiding: false,
        size: 40,
      },
      {
        id: 'domain',
        header: '域名',
        cell: ({ row }) => {
          const d = row.original
          const isIdn = d.domain_ascii !== d.domain_unicode && d.domain_unicode !== ''
          return (
            <div className="flex min-w-0 items-center gap-1.5">
              <button
                onClick={() => setDetailId(d.id)}
                className="truncate font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {isIdn ? d.domain_unicode : d.domain_ascii}
              </button>
              {isIdn && (
                <Tooltip content={`Punycode：${d.domain_ascii}`}>
                  <Badge variant="outline" className="shrink-0 text-[10px]">IDN</Badge>
                </Tooltip>
              )}
              <Tooltip content="复制域名">
                <button
                  onClick={() => {
                    void navigator.clipboard.writeText(d.domain_ascii)
                    toast.success('已复制', d.domain_ascii)
                  }}
                  className="shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus:opacity-100 group-hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`复制 ${d.domain_ascii}`}
                >
                  <Copy className="h-3 w-3" aria-hidden />
                </button>
              </Tooltip>
            </div>
          )
        },
      },
      {
        id: 'tld',
        header: 'TLD',
        cell: ({ row }) => <span className="text-xs text-muted-foreground">.{row.original.tld}</span>,
      },
      {
        id: 'registrar',
        header: '当前注册商',
        cell: ({ row }) => {
          const observed = row.original.current_registrar?.trim()
          const ianaID = row.original.registrar_iana_id?.trim()
          // The directory's display name wins — setting one is exactly a
          // request to see it here instead of the registry's own wording.
          const display = row.original.registrar_display?.trim() || observed
          if (!display) return <span className="text-xs text-muted-foreground">—</span>
          return (
            // Wide enough to reach what distinguishes one registrar from
            // another. Registrar names share long leading boilerplate and
            // differ near the end — "Alibaba Cloud Computing Ltd. d/b/a
            // HiChina" and "Alibaba Cloud Computing (Beijing) Co., Ltd." are
            // two different companies that a 180px truncation rendered as the
            // same string, so the column read as duplicated rows.
            <Tooltip
              content={
                // The registry's own wording stays reachable: it is what an
                // operator compares against when a mapping looks wrong.
                (observed && observed !== display ? `${display}\n注册局返回：${observed}` : display) +
                (ianaID ? `\nIANA ${ianaID}` : '')
              }
            >
              <span className="block max-w-[260px] truncate text-xs">{display}</span>
            </Tooltip>
          )
        },
      },
      {
        // One column, not three booleans: a name is pinyin, or an initial
        // abbreviation, or an auspicious number — almost never two at once —
        // and three mostly-empty columns would cost more width than they earn.
        id: 'zh',
        header: '中文语义',
        cell: ({ row }) => {
          const d = row.original
          const tags: React.ReactNode[] = []
          // Three sources, one column, in the order that decides which answer a
          // reader acts on: what a person typed, then the model, then the rules.
          // Each is marked, because they are not equally trustworthy — the rules
          // are reproducible, the model is not, and a person's entry outranks
          // both by construction.
          const reading = d.zh_manual_reading || d.zh_ai_reading
          const readingMark = d.zh_manual_reading ? '人' : d.zh_ai_reading ? 'AI' : ''
          if (reading) {
            tags.push(
              <span key="r" className="text-xs font-medium">{reading}</span>,
              <Badge
                key="rb"
                variant={d.zh_manual_reading ? 'default' : 'secondary'}
                className="text-[10px]"
              >
                {readingMark}
              </Badge>,
            )
          } else if (d.zh_in_dictionary && d.zh_words) {
            // The characters, not a badge saying "pinyin": 淘宝 is a fact a
            // reader can check, "2 音节" is one they cannot act on.
            tags.push(
              <span key="w" className="text-xs font-medium">{d.zh_words.split(' ')[0]}</span>,
            )
          }
          if (tags.length === 0) return <span className="text-xs text-muted-foreground">—</span>
          return (
            // The note is the reading itself — "tao-bao(2音节)" or
            // "888888：888=发发发". A badge asserts; the note explains, and on
            // an ambiguous name it is the only place the other readings appear.
            <Tooltip content={<ZHDetail d={d} />}>
              <span className="flex flex-wrap gap-1">{tags}</span>
            </Tooltip>
          )
        },
      },
      {
        id: 'length',
        header: '长度',
        cell: ({ row }) => <span className="tabular text-xs">{row.original.label_length}</span>,
      },
      {
        id: 'charset',
        header: '字符集',
        cell: ({ row }) => <span className="text-xs">{charsetLabel(row.original.charset_class)}</span>,
      },
      {
        id: 'features',
        header: '特征',
        // The operator's own tags, not the built-in feature bits. 全同/回文/
        // 纯字母 were computed by us and could not be renamed, retired or
        // reasoned about by the person reading them; the tag rules are theirs.
        cell: ({ row }) => <LabelTags tags={row.original.zh_tags} />,
      },
      {
        id: 'status',
        header: '当前状态',
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <StatusBadge
              status={row.original.current_status}
              source={row.original.status_source}
              confirmedAt={row.original.last_resolved_at}
              confidence={row.original.status_confidence}
            />
            <GfwBadge status={row.original.gfw_risk_status} />
          </div>
        ),
      },
      {
        id: 'registered_at',
        header: '注册时间',
        cell: ({ row }) =>
          row.original.registered_at ? (
            <span className="text-xs">{formatDate(row.original.registered_at)}</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: 'dates',
        header: '到期时间',
        cell: ({ row }) => {
          const d = row.original
          if (!d.expires_at && !d.drop_estimate_at) {
            return <span className="text-xs text-muted-foreground">—</span>
          }
          return (
            <div className="text-xs">
              {d.expires_at && <div>{formatDate(d.expires_at)}</div>}
              {d.drop_estimate_at && (
                <Tooltip content={`预计释放来源：${d.drop_estimate_source ?? '未知'}；为估算值，不构成承诺`}>
                  <div className="text-muted-foreground">预计释放 {formatDate(d.drop_estimate_at)}</div>
                </Tooltip>
              )}
            </div>
          )
        },
      },
      {
        id: 'registered_years',
        header: '已注册',
        cell: ({ row }) => {
          const years = registeredYears(row.original.registered_at)
          if (years === null) return <span className="text-xs text-muted-foreground">—</span>
          return <span className="whitespace-nowrap text-xs tabular-nums">{years} 年</span>
        },
      },
      {
        id: 'expired_days',
        header: '已过期',
        cell: ({ row }) => {
          // Only while a name is on its way down. 已注册 still holds it (a past
          // expiry there is auto-renew grace, or a registry-held name like
          // example.com); 可注册 means it already dropped, so counting days
          // since expiry says nothing about what to do now; 保留 never drops;
          // 未知 would be asserting more than was observed.
          const days = DROPPING_STATUSES.has(row.original.current_status)
            ? expiredDays(row.original.expires_at)
            : null
          if (days === null) return <span className="text-xs text-muted-foreground">—</span>
          return (
            <span className="whitespace-nowrap text-xs tabular-nums">
              {formatNumber(days)} 天
            </span>
          )
        },
      },
      {
        id: 'release_estimate',
        header: '可注册倒计时',
        cell: ({ row }) => (
          <ReleaseEstimateCell domain={row.original} timezone={user?.timezone} />
        ),
      },
      {
        id: 'freshness',
        header: '最后确认',
        cell: ({ row }) => (
          <FreshnessCell
            lastResolvedAt={row.original.last_resolved_at}
            tier={row.original.monitor_tier}
            nextCheckAt={row.original.next_check_at}
            watched={row.original.watched}
            timezone={user?.timezone}
          />
        ),
      },
      {
        // Split from a single 首次/最后发现 cell: one header cannot sort two
        // dates, and dropping the sort dropdown would otherwise have taken
        // 按首次发现排序 with it. Two headers also say which date is which.
        id: 'first_seen',
        header: '首次发现',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">{formatDate(row.original.first_seen_at)}</span>
        ),
      },
      {
        id: 'seen',
        header: '最后发现',
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">{formatDate(row.original.last_seen_at)}</span>
        ),
      },
      {
        id: 'sources',
        header: '来源',
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.sources?.map((s) => (
              <Tooltip key={s} content={sourceLabel(s)}>
                <Badge variant="outline" className="text-[10px] font-normal">{s}</Badge>
              </Tooltip>
            )) ?? <span className="text-xs text-muted-foreground">—</span>}
          </div>
        ),
      },
      {
        id: 'matches',
        header: '命中规则',
        cell: ({ row }) => {
          const d = row.original
          if (!d.match_count) return <span className="text-xs text-muted-foreground">—</span>
          return (
            <Tooltip content={d.matched_rules?.join('、') ?? ''}>
              <Badge variant="secondary">{d.match_count} 条</Badge>
            </Tooltip>
          )
        },
      },
      {
        id: 'watch',
        header: () => <div className="text-center">操作</div>,
        // The column picker needs a name it can print. A function header
        // stringifies to its own source, which is what it used to show.
        meta: { label: '操作' },
        cell: ({ row }) => {
          const d = row.original
          return (
            <div className="flex items-center justify-center gap-1">
              {d.watched ? (
                <Badge variant={d.watcher_priority === 'critical' ? 'danger' : 'success'}>
                  {d.watcher_state === 'observed_registered' ? '已注册·观察中' : '监控中'}
                </Badge>
              ) : (
                <Tooltip content="加入监控">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => addWatcher(d)}
                    disabled={createWatcher.isPending}
                    aria-label={`加入监控 ${d.domain_ascii}`}
                  >
                    <Eye className="h-4 w-4" aria-hidden />
                  </Button>
                </Tooltip>
              )}
              <Tooltip content={d.favorite ? '取消关注' : '关注'}>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => toggleFavorite(d)}
                  disabled={favoriteDomain.isPending || unfavoriteDomain.isPending}
                  aria-label={d.favorite ? `取消关注 ${d.domain_ascii}` : `关注 ${d.domain_ascii}`}
                >
                  <Heart
                    className={cn('h-4 w-4', d.favorite && 'fill-current text-primary')}
                    aria-hidden
                  />
                </Button>
              </Tooltip>
              {/* Edit and delete live behind the menu: they are rare, and one
                  of them is irreversible. Keeping them a click away stops a
                  mis-aimed click in a dense row from deleting a domain. */}
              {user?.role === 'admin' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`${d.domain_ascii} 的更多操作`}
                    >
                      <MoreHorizontal className="h-4 w-4" aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => setEditingDomain(d)}>
                      <Pencil className="h-4 w-4" aria-hidden />
                      编辑域名资料
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem destructive onSelect={() => setDeletingDomain(d)}>
                      <Trash2 className="h-4 w-4" aria-hidden />
                      删除域名
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )
        },
      },
      {
        id: 'tier',
        header: '调度层级',
        cell: ({ row }) => <TierBadge tier={row.original.monitor_tier} />,
      },
      {
        id: 'score',
        header: '价值分',
        cell: ({ row }) =>
          row.original.intrinsic_value_score != null ? (
            <Tooltip content="价值分用于排序与调度建议，不构成估值或收益承诺">
              <span className="tabular text-xs font-medium">
                {row.original.intrinsic_value_score.toFixed(1)}
              </span>
            </Tooltip>
          ) : (
            <span className="text-xs text-muted-foreground">未计算</span>
          ),
      },
    ],
    [selected, user?.timezone, user?.role, createWatcher.isPending, favoriteDomain.isPending, unfavoriteDomain.isPending],
  )

  const table = useReactTable({
    data: data?.items ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  })

  async function addWatcher(d: Domain) {
    try {
      const res = await createWatcher.mutateAsync({ domain_id: d.id })
      toast.success(`已加入监控 ${d.domain_ascii}`, res.notice || undefined)
    } catch (err) {
      const message = err instanceof ApiError ? err.message : '加入监控失败'
      toast.error('无法加入监控', message)
    }
  }

  async function toggleFavorite(d: Domain) {
    try {
      if (d.favorite) {
        await unfavoriteDomain.mutateAsync(d.id)
        toast.success(`已取消关注 ${d.domain_ascii}`)
      } else {
        await favoriteDomain.mutateAsync(d.id)
        toast.success(`已关注 ${d.domain_ascii}`)
      }
    } catch (err) {
      const message = err instanceof ApiError ? err.message : '更新关注状态失败'
      toast.error('操作失败', message)
    }
  }

  async function exportSelection() {
    const ruleId = params.get('rule_id')
    const watched = params.get('watched') === 'true'
    const favorite = params.get('favorite') === 'true'
    if (!ruleId && !watched && !favorite) {
      // §15.4 forbids a full shared-library export, so the UI never offers one.
      toast.warning(
        '导出需要限定范围',
        '仅支持导出本人规则命中、已关注或监控中的域名。',
      )
      return
    }
    try {
      await download('/domains/export', { rule_id: ruleId ?? undefined, watched, favorite }, 'domains.csv')
      toast.success('导出已开始', '导出行数计入 export_rows 额度')
    } catch (err) {
      toast.error('导出失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  const selectedIds = Object.keys(selected)

  return (
    <>
      <PageHeader
        title="域名库"
        description={
          <span>
            全平台共享主数据。
            {data?.total != null && (
              <>
                {' '}当前筛选约 {formatNumber(data.total)}
                {data.total_is_estimate ? '+ 条（估算）' : ' 条'}
              </>
            )}
          </span>
        }
        actions={
          <>
            {user?.role === 'admin' && (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-3.5 w-3.5" aria-hidden /> 新增域名
              </Button>
            )}
            {/* 刷新 stays out of the menu: it is the one action worth reaching
                without a click first, and it carries the loading state that
                tells you the table is refetching. */}
            <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
              <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} aria-hidden /> 刷新
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon-sm" aria-label="更多操作">
                  <MoreHorizontal className="h-4 w-4" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setShowColumns((v) => !v)}>
                  <Columns3 className="h-3.5 w-3.5" aria-hidden /> 列
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => {
                    void navigator.clipboard.writeText(window.location.href)
                    toast.success('已复制当前视图链接')
                  }}
                >
                  <Share2 className="h-3.5 w-3.5" aria-hidden /> 分享
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void exportSelection()}>
                  <Download className="h-3.5 w-3.5" aria-hidden /> 导出
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      {/* One row: query on the left, refinements on the right. This only fits
          because the triggers size to their own content — the two-row layout
          existed to rescue five fixed-width boxes that no longer exist. */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          className="flex w-full max-w-xs"
          onSubmit={(e) => {
            e.preventDefault()
            updateParam('q', searchDraft || undefined)
          }}
        >
          <div className="relative w-full">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden />
            <Input
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder="搜索域名或主 label…"
              className="h-9 pl-8"
              aria-label="搜索域名"
            />
            {searchDraft && (
              <button
                type="button"
                onClick={() => { setSearchDraft(''); updateParam('q', undefined) }}
                aria-label="清除搜索"
                className="absolute right-2 top-2.5 rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            )}
          </div>
        </form>

        <div className="flex flex-wrap items-center gap-2">
        <FilterSelect
          label="当前状态"
          options={STATUS_OPTIONS}
          selected={params.getAll('status')}
          onToggle={(v) => toggleMulti('status', v)}
          onClear={() => updateParam('status', undefined)}
        />

        <FilterSelect
          label="后缀"
          options={tldOptions}
          selected={params.getAll('tld')}
          onToggle={(v) => toggleMulti('tld', v)}
          onClear={() => updateParam('tld', undefined)}
        />

        <FilterSelect
          label="注册商"
          menuClassName="w-[min(380px,calc(100vw-3rem))]"
          searchable
          searchPlaceholder="搜索注册商…"
          emptyText={registrarOptions.length ? '没有匹配的注册商' : '暂无注册商数据'}
          options={registrarOptions}
          selected={params.getAll('registrar_id')}
          onToggle={(v) => toggleMulti('registrar_id', v)}
          onClear={() => updateParam('registrar_id', undefined)}
        />

        <DateRangeFilter params={params} onApply={updateParams} />

        <LengthFilter params={params} onApply={updateParams} />

        <FilterSelect
          label="标签"
          options={(tagVocab ?? []).map((t) => ({ value: t, label: t }))}
          selected={params.getAll('tag')}
          onToggle={(v) => toggleMulti('tag', v)}
          onClear={() => updateParam('tag', undefined)}
          emptyText="还没有标签规则，先去语义库建一条"
        />

        <FilterSelect
          label="中文语义"
          options={ZH_OPTIONS}
          selected={ZH_OPTIONS.filter((o) => params.get(o.param) === o.on).map((o) => o.value)}
          onToggle={(v) => {
            const opt = ZH_OPTIONS.find((o) => o.value === v)
            if (!opt) return
            updateParam(opt.param, params.get(opt.param) === opt.on ? undefined : opt.on)
          }}
          onClear={() => updateParams(
            Object.fromEntries(ZH_OPTIONS.map((o) => [o.param, undefined])),
          )}
        />

        <FilterSelect
          label="字符集"
          options={CHARSET_OPTIONS}
          selected={params.getAll('charset')}
          onToggle={(v) => toggleMulti('charset', v)}
          onClear={() => updateParam('charset', undefined)}
        />

        <FilterToggle
          label="已关注"
          active={params.get('favorite') === 'true'}
          onToggle={() => updateParam('favorite', params.get('favorite') === 'true' ? undefined : 'true')}
        />

        {activeFilters > 0 && (
          <Button
            variant="ghost" size="sm"
            className="ml-auto"
            onClick={() => { setParams(new URLSearchParams(), { replace: true }); setSearchDraft('') }}
          >
            <X className="h-3.5 w-3.5" aria-hidden /> 清除全部
          </Button>
        )}
        </div>
      </div>

      {showColumns && (
        <div className="mb-3 rounded-lg border bg-card p-3">
          <p className="mb-2 text-xs font-medium text-muted-foreground">显示列（偏好会自动保存）</p>
          <div className="flex flex-wrap gap-3">
            {table.getAllLeafColumns().filter((c) => c.getCanHide()).map((column) => (
              <label key={column.id} className="flex items-center gap-1.5 text-sm">
                <Checkbox
                  checked={column.getIsVisible()}
                  onCheckedChange={(v) => column.toggleVisibility(Boolean(v))}
                />
                {columnLabel(column.columnDef)}
              </label>
            ))}
          </div>
        </div>
      )}


      {selectedIds.length > 0 && (
        <div className="mb-3 flex items-center gap-3 rounded-lg border bg-primary/5 px-4 py-2">
          <span className="text-sm">已选择 {selectedIds.length} 个域名</span>
          <Button
            size="sm" variant="outline"
            onClick={async () => {
              const rows = (data?.items ?? []).filter((d) => selected[d.id] && !d.favorite)
              let ok = 0
              for (const d of rows) {
                try { await favoriteDomain.mutateAsync(d.id); ok++ }
                catch { break }
              }
              toast.success(`已关注 ${ok} 个域名`)
              setSelected({})
            }}
          >
            <Heart className="h-3.5 w-3.5" aria-hidden /> 批量关注
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={async () => {
              const rows = (data?.items ?? []).filter((d) => selected[d.id] && !d.watched)
              let ok = 0
              for (const d of rows) {
                try { await createWatcher.mutateAsync({ domain_id: d.id }); ok++ }
                catch { break }
              }
              toast.success(`已加入监控 ${ok} 个`, ok < rows.length ? '部分域名因套餐上限未能加入' : undefined)
              setSelected({})
            }}
          >
            <Eye className="h-3.5 w-3.5" aria-hidden /> 批量加入监控
          </Button>
          {user?.role === 'admin' && (
            <Button
              size="sm"
              variant="outline"
              className="text-destructive hover:text-destructive"
              onClick={() => setConfirmBulkDelete(true)}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden /> 批量删除
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setSelected({})}>取消选择</Button>
        </div>
      )}

      {/* Typing the count is the confirmation. The selection can span pages and
          survive a filter change, so the number on screen is the thing most
          likely to be wrong — and the one a checkbox would never surface. */}
      <Dialog open={confirmBulkDelete} onOpenChange={(o) => !o && setConfirmBulkDelete(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>删除选中的 {selectedIds.length} 个域名？</DialogTitle>
            <DialogDescription>
              域名是共享数据。删除会同时移除它们的监控、规则命中、观测与生命周期事件，
              包括其他用户建立的。此操作不可撤销。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="bulk-delete-confirm">
              请输入 <span className="font-mono font-semibold">{selectedIds.length}</span> 以确认
            </Label>
            <Input
              id="bulk-delete-confirm"
              value={bulkDeleteConfirm}
              onChange={(e) => setBulkDeleteConfirm(e.target.value)}
              inputMode="numeric"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmBulkDelete(false)}>取消</Button>
            <Button
              variant="destructive"
              disabled={bulkDelete.isPending || bulkDeleteConfirm !== String(selectedIds.length)}
              onClick={async () => {
                try {
                  const res = await bulkDelete.mutateAsync(selectedIds)
                  toast.success('已删除', res.notice)
                  setSelected({})
                } catch (err) {
                  toast.error('删除失败', err instanceof ApiError ? err.message : undefined)
                } finally {
                  setConfirmBulkDelete(false)
                  setBulkDeleteConfirm('')
                }
              }}
            >
              {bulkDelete.isPending ? '删除中…' : '确认删除'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : isLoading ? (
        <LoadingState rows={8} label="正在加载域名库" />
      ) : (data?.items.length ?? 0) === 0 ? (
        <EmptyState
          title="没有符合条件的域名"
          description="调整筛选条件，或先在域名查询中导入域名、运行规则以发现候选。"
          icon={Search}
        />
      ) : (
        <>
          {/* The estimate is already stated under the page title as
              「约 N+ 条（估算）」; a banner repeating it took a strip of the
              page to say the same thing a second time. */}
          <div className="rounded-lg border bg-card">
            <div className="scroll-x">
              <table className="w-full min-w-[1420px] text-sm">
                <caption className="sr-only">
                  域名库结果表格，共 {data?.items.length ?? 0} 行
                </caption>
                <thead className="border-b bg-muted/40">
                  {table.getHeaderGroups().map((hg) => (
                    <tr key={hg.id}>
                      {hg.headers.map((header) => {
                        const sortable = SORTABLE_COLUMNS[header.column.id]
                        const active = columnSort(header.column.id)
                        const label = header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())
                        return (
                          <th
                            key={header.id}
                            scope="col"
                            aria-sort={
                              !sortable ? undefined : active ? (active.desc ? 'descending' : 'ascending') : 'none'
                            }
                            className={cn(
                              'whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-muted-foreground',
                              header.column.id === 'release_estimate' && 'w-px',
                            )}
                          >
                            {sortable && !header.isPlaceholder ? (
                              <button
                                type="button"
                                onClick={() => toggleSort(header.column.id)}
                                className={cn(
                                  'group/sort -mx-1 flex items-center gap-1 rounded px-1 py-0.5 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                  active && 'text-foreground',
                                )}
                              >
                                {label}
                                {active ? (
                                  active.desc ? (
                                    <ArrowDown className="h-3 w-3 shrink-0" aria-hidden />
                                  ) : (
                                    <ArrowUp className="h-3 w-3 shrink-0" aria-hidden />
                                  )
                                ) : (
                                  <ArrowUpDown
                                    className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover/sort:opacity-50"
                                    aria-hidden
                                  />
                                )}
                              </button>
                            ) : (
                              label
                            )}
                          </th>
                        )
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="group border-b last:border-0 hover:bg-muted/40">
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={cn('px-3 py-2 align-middle', cell.column.id === 'release_estimate' && 'w-px whitespace-nowrap')}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              显示 {data?.items.length ?? 0} 条
              {isFetching && <span className="ml-2">更新中…</span>}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline" size="sm"
                disabled={cursorStack.length === 0}
                onClick={() => {
                  const stack = [...cursorStack]
                  stack.pop()
                  const prev = stack[stack.length - 1]
                  setCursorStack(stack)
                  const next = new URLSearchParams(params)
                  if (prev) next.set('cursor', prev)
                  else next.delete('cursor')
                  setParams(next, { replace: true })
                }}
              >
                上一页
              </Button>
              <Button
                variant="outline" size="sm"
                disabled={!data?.next_cursor}
                onClick={() => {
                  if (!data?.next_cursor) return
                  setCursorStack((s) => [...s, data.next_cursor!])
                  const next = new URLSearchParams(params)
                  next.set('cursor', data.next_cursor)
                  setParams(next, { replace: true })
                }}
              >
                下一页
              </Button>
            </div>
          </div>
        </>
      )}

      <DomainDetailDrawer domainId={detailId} onClose={() => setDetailId(null)} />
      {user?.role === 'admin' && (
        <>
          <AdminDomainDialog open={createOpen} onOpenChange={setCreateOpen} />
          <AdminDomainDialog
            open={Boolean(editingDomain)}
            onOpenChange={(open) => !open && setEditingDomain(null)}
            domain={editingDomain ?? undefined}
          />
          <AdminDeleteDomainDialog
            domain={deletingDomain}
            onOpenChange={(open) => !open && setDeletingDomain(null)}
          />
        </>
      )}
    </>
  )
}

function AdminDomainDialog({
  open, onOpenChange, domain,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  domain?: Domain
}) {
  const toast = useToast()
  const createDomain = useAdminCreateDomain()
  const updateDomain = useAdminUpdateDomain()
  const editing = Boolean(domain)
  const [name, setName] = React.useState('')
  const [status, setStatus] = React.useState<string>(domain?.current_status ?? 'registered')
  const [expiresAt, setExpiresAt] = React.useState(toLocalDateTimeValue(domain?.expires_at))
  const [registrar, setRegistrar] = React.useState(domain?.current_registrar ?? '')

  React.useEffect(() => {
    if (!open) return
    setName(domain?.domain_ascii ?? '')
    setStatus(domain?.current_status ?? 'registered')
    setExpiresAt(toLocalDateTimeValue(domain?.expires_at))
    setRegistrar(domain?.current_registrar ?? '')
  }, [open, domain])

  const pending = createDomain.isPending || updateDomain.isPending

  // Fill the form from the registry rather than from memory. Typing a status,
  // an expiry and a registrar by hand is three chances to store something the
  // registry never said — and once saved it is indistinguishable from a
  // confirmed observation.
  const lookup = useAdminLookupDomain()
  async function autofill() {
    const target = (editing ? domain?.domain_ascii : name)?.trim()
    if (!target) {
      toast.warning('请先填写域名')
      return
    }
    try {
      const r = await lookup.mutateAsync(target)
      if (!r.found) {
        toast.warning('查询未成功，请手动填写', r.error)
        return
      }
      if (r.status) setStatus(r.status)
      setExpiresAt(toLocalDateTimeValue(r.expires_at))
      setRegistrar(r.registrar ?? '')
      toast.success('已按注册局数据填充', r.raw_statuses?.join(', '))
    } catch (err) {
      toast.error('查询失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    const form = new FormData(event.currentTarget as HTMLFormElement)
    const submittedExpiry = String(form.get('expires_at') ?? '').trim()
    const body: AdminDomainInput = {
      status,
      expires_at: submittedExpiry ? new Date(submittedExpiry).toISOString() : null,
      current_registrar: String(form.get('current_registrar') ?? '').trim(),
      ...(!editing ? { domain: String(form.get('domain') ?? '').trim() } : {}),
    }
    try {
      if (domain) {
        await updateDomain.mutateAsync({ id: domain.id, body })
        toast.success('域名资料已更新', domain.domain_ascii)
      } else {
        const created = await createDomain.mutateAsync(body)
        toast.success('域名已新增', created.domain)
      }
      onOpenChange(false)
    } catch (err) {
      toast.error(editing ? '更新失败' : '新增失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{editing ? '编辑域名资料' : '新增域名'}</DialogTitle>
            <DialogDescription>
              {editing
                ? '域名名称不直接改名；如需更换，请删除后重新新增。'
                : '管理员录入会写入共享域名库，并标记为“管理员录入”。'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor={editing ? 'admin-domain-readonly' : 'admin-domain-name'}>域名</Label>
            <div className="flex gap-2">
              <Input
                id={editing ? 'admin-domain-readonly' : 'admin-domain-name'}
                name="domain"
                value={editing ? domain?.domain_ascii ?? '' : name}
                onChange={(e) => setName(e.target.value)}
                placeholder="example.xyz"
                disabled={editing}
                required
                autoFocus={!editing}
              />
              <Button
                type="button"
                variant="outline"
                onClick={autofill}
                disabled={lookup.isPending}
                className="shrink-0"
              >
                {lookup.isPending ? '查询中…' : '自动获取'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              点「自动获取」按注册局返回的数据填充下面三项，避免手工录入与实际不符。
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>当前状态</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger aria-label="当前状态"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="admin-domain-expires">到期时间</Label>
            <DateTimePicker
              id="admin-domain-expires"
              name="expires_at"
              aria-label="到期时间"
              placeholder="选择到期日期"
              value={expiresAt}
              onChange={setExpiresAt}
            />
            <p className="text-xs text-muted-foreground">
              按本机时区（{localZoneLabel()}）填写，留空表示未知或不适用。
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="admin-domain-registrar">当前注册商</Label>
            <Input
              id="admin-domain-registrar"
              name="current_registrar"
              value={registrar}
              onChange={(e) => setRegistrar(e.target.value)}
              placeholder="例如 Spaceship, Inc."
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
            <Button type="submit" disabled={pending || (!editing && !name.trim())}>
              {pending ? '保存中…' : '保存'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function AdminDeleteDomainDialog({
  domain, onOpenChange,
}: {
  domain: Domain | null
  onOpenChange: (open: boolean) => void
}) {
  const toast = useToast()
  const deleteDomain = useAdminDeleteDomain()
  const [confirm, setConfirm] = React.useState('')

  React.useEffect(() => {
    if (domain) setConfirm('')
  }, [domain])

  async function remove() {
    if (!domain || confirm.trim().toLowerCase() !== domain.domain_ascii.toLowerCase()) return
    try {
      await deleteDomain.mutateAsync({ id: domain.id, confirm_domain: confirm.trim() })
      toast.success('域名已删除', domain.domain_ascii)
      onOpenChange(false)
    } catch (err) {
      toast.error('删除失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  return (
    <Dialog open={Boolean(domain)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>确认删除域名？</DialogTitle>
          <DialogDescription>
            删除会同时清理该域名关联的观察、证据、规则命中和用户关注，不可撤销。
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="admin-delete-confirm">输入 {domain?.domain_ascii} 以确认</Label>
          <Input
            id="admin-delete-confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="off"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>取消</Button>
          <Button
            variant="destructive"
            onClick={() => void remove()}
            disabled={deleteDomain.isPending || confirm.trim().toLowerCase() !== domain?.domain_ascii.toLowerCase()}
          >
            {deleteDomain.isPending ? '删除中…' : '确认删除'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Names the timezone the expiry field actually works in.
 *
 * The label used to promise 北京时间 while both the fill-in and the submit ran
 * through the browser's own zone, so for anyone not on UTC+8 it named a time
 * the form never used. The round trip was self-consistent — you got back what
 * you typed — but the label had to stop claiming otherwise.
 */
function localZoneLabel(): string {
  const offset = -new Date().getTimezoneOffset() / 60
  const sign = offset >= 0 ? '+' : '−'
  return `UTC${sign}${Number.isInteger(offset) ? Math.abs(offset) : Math.abs(offset).toFixed(1)}`
}

function toLocalDateTimeValue(value?: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 16)
}

function ReleaseEstimateCell({ domain, timezone }: { domain: Domain; timezone?: string }) {
  if (!['expired', 'redemptionPeriod', 'pendingDelete'].includes(domain.current_status)) {
    return <span className="text-xs text-muted-foreground">—</span>
  }
  if (!domain.release_estimate_start_at) {
    return <span className="text-xs text-muted-foreground">—</span>
  }
  const now = Date.now()
  const start = Date.parse(domain.release_estimate_start_at)
  if (!Number.isFinite(start)) {
    return <span className="text-xs text-muted-foreground">—</span>
  }

  const day = 86_400_000
  const remainingDays = now < start ? Math.max(1, Math.ceil((start - now) / day)) : 0

  const samples = domain.release_estimate_samples ?? 0
  return (
    <Tooltip
      content={
        <div className="space-y-1">
          <p>倒计时采用历史最短周期：{formatDateTime(domain.release_estimate_start_at, timezone)}</p>
          {domain.release_estimate_end_at && (
            <p>历史周期上界：{formatDateTime(domain.release_estimate_end_at, timezone)}</p>
          )}
          <p>依据：同注册商、同后缀的 {samples} 条释放或重新注册样本</p>
          <p className="text-muted-foreground">未捕获开放时刻的样本按重新注册时间计算上界，不代表注册商保证。</p>
        </div>
      }
    >
      <button className="whitespace-nowrap rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <span className="whitespace-nowrap text-xs tabular-nums">{remainingDays} 天</span>
      </button>
    </Tooltip>
  )
}

/**
 * The date columns a range can be applied to.
 *
 * One control rather than three separate range filters: they answer the same
 * shape of question and are never useful at once, so making the column part of
 * the filter keeps the toolbar from growing a row per date in the schema.
 */
const DATE_FIELDS = [
  { value: 'expires', label: '到期时间', from: 'expires_from', to: 'expires_to' },
  { value: 'drop', label: '预计释放', from: 'drop_from', to: 'drop_to' },
  { value: 'last_seen', label: '最后观察', from: 'last_seen_from', to: 'last_seen_to' },
] as const

/** Ranges worth one click, expressed in days from now; null means open-ended. */
const DATE_PRESETS = [
  { label: '未来 7 天', from: 0, to: 7 },
  { label: '未来 30 天', from: 0, to: 30 },
  { label: '未来 90 天', from: 0, to: 90 },
  { label: '过去 7 天', from: -7, to: 0 },
  { label: '过去 30 天', from: -30, to: 0 },
]

function dayOffsetISO(days: number): string {
  return new Date(Date.now() + days * 86_400_000).toISOString()
}

/** `YYYY-MM-DD` for a date input, from an ISO instant. */
function toDateInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : toLocalDateTimeValue(iso).slice(0, 10)
}

function DateRangeFilter({
  params, onApply,
}: { params: URLSearchParams; onApply: (patch: Record<string, string | undefined>) => void }) {
  // The active field is whichever one currently carries a value, so reopening
  // the menu lands on the filter that is actually in effect.
  const activeField = DATE_FIELDS.find((f) => params.get(f.from) || params.get(f.to)) ?? DATE_FIELDS[0]
  const [field, setField] = React.useState<typeof DATE_FIELDS[number]>(activeField)
  React.useEffect(() => { setField(activeField) }, [activeField.value]) // eslint-disable-line react-hooks/exhaustive-deps

  const from = params.get(field.from)
  const to = params.get(field.to)
  const active = Boolean(from || to)

  function clearAll(): Record<string, string | undefined> {
    return Object.fromEntries(DATE_FIELDS.flatMap((f) => [[f.from, undefined], [f.to, undefined]]))
  }

  function apply(nextFrom?: string, nextTo?: string) {
    // Clearing every field first keeps two date columns from being filtered at
    // once when the user switches which column they are asking about.
    onApply({ ...clearAll(), [field.from]: nextFrom, [field.to]: nextTo })
  }

  const summary = active
    ? `${field.label} ${toDateInput(from) || '不限'} ~ ${toDateInput(to) || '不限'}`
    : undefined

  return (
    <FilterPopover
      label="时间范围"
      active={active}
      summary={summary}
      onClear={() => onApply(clearAll())}
    >
      <div className="space-y-3">
        <div className="flex flex-wrap gap-1">
          {DATE_FIELDS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setField(f)}
              className={cn(
                'rounded-md border px-2 py-1 text-xs transition-colors',
                f.value === field.value ? 'border-primary bg-primary/10 font-medium text-primary' : 'hover:bg-accent',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="space-y-1 text-xs text-muted-foreground">
            <span>起</span>
            <Input
              type="date"
              className="h-8"
              value={toDateInput(from)}
              onChange={(e) => apply(
                e.target.value ? new Date(`${e.target.value}T00:00`).toISOString() : undefined,
                to ?? undefined,
              )}
            />
          </label>
          <label className="space-y-1 text-xs text-muted-foreground">
            <span>止</span>
            <Input
              type="date"
              className="h-8"
              value={toDateInput(to)}
              onChange={(e) => apply(
                from ?? undefined,
                // Inclusive of the chosen day: a range ending "on the 20th"
                // that stopped at 00:00 would exclude the whole of the 20th.
                e.target.value ? new Date(`${e.target.value}T23:59:59`).toISOString() : undefined,
              )}
            />
          </label>
        </div>

        <div className="flex flex-wrap gap-1 border-t pt-2">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => apply(dayOffsetISO(p.from), dayOffsetISO(p.to))}
              className="rounded-md border px-2 py-1 text-xs hover:bg-accent"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
    </FilterPopover>
  )
}

function LengthFilter({
  params, onApply,
}: { params: URLSearchParams; onApply: (patch: Record<string, string | undefined>) => void }) {
  const min = params.get('length_min') ?? ''
  const max = params.get('length_max') ?? ''
  const active = Boolean(min || max)

  return (
    <FilterPopover
      label="长度"
      active={active}
      summary={active ? `长度 ${min || '不限'}–${max || '不限'}` : undefined}
      onClear={() => onApply({ length_min: undefined, length_max: undefined })}
      menuClassName="w-64"
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <label className="space-y-1 text-xs text-muted-foreground">
            <span>最小长度</span>
            <Input
              type="number" min={1} max={63} className="h-8"
              defaultValue={min}
              key={`min-${min}`}
              onBlur={(e) => onApply({ length_min: e.target.value || undefined })}
            />
          </label>
          <label className="space-y-1 text-xs text-muted-foreground">
            <span>最大长度</span>
            <Input
              type="number" min={1} max={63} className="h-8"
              defaultValue={max}
              key={`max-${max}`}
              onBlur={(e) => onApply({ length_max: e.target.value || undefined })}
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-1 border-t pt-2">
          {[2, 3, 4, 5, 6].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onApply({ length_min: String(n), length_max: String(n) })}
              className="rounded-md border px-2 py-1 text-xs hover:bg-accent"
            >
              {n} 位
            </button>
          ))}
        </div>
      </div>
    </FilterPopover>
  )
}


function charsetLabel(c: string): string {
  const map: Record<string, string> = {
    digits: '纯数字', letters: '纯字母', alphanumeric: '字母数字',
    hyphenated: '含连字符', idn: 'IDN', other: '其他',
  }
  return map[c] ?? c
}
